import type { Middleware, Reducer } from "@reduxjs/toolkit"

import type { WorkflowState } from "@/modules/Workflow/store/types"

export interface WorkflowStoreRegistration {
  reducer: Reducer<WorkflowState>
  /**
   * A THUNK so the middleware is built on the first dispatched action rather
   * than while the slice module is still evaluating. Registration happens at
   * that module's top level, and the middleware is constructed from the slice's
   * own action creators.
   */
  resolveUndoMiddleware: () => Middleware
}

/**
 * The seam between the root store and the workflow canvas.
 *
 * 🚨 THIS FILE IS A LEAF ON PURPOSE, AND ITS ONLY RUNTIME IMPORT IS NONE.
 * `modules/Workflow/store/reducers/workflow` imports `addEdge`,
 * `applyEdgeChanges` and `applyNodeChanges` from `@xyflow/react` as VALUES, so
 * naming the workflow slice in `configureStore` put the 172 kB canvas library
 * and its stylesheet in `index.html`'s modulepreload set — paid by the public
 * `/embed/:embed-id` widget, which renders no canvas. The store therefore starts
 * with a placeholder and the slice mounts itself when its own chunk evaluates.
 *
 * The indirection goes through here rather than through `@/store` directly so
 * the SLICE never imports the ROOT STORE. Importing it would boot
 * `configureStore`, `initializeSharedConfig()` and the websocket middleware
 * inside every pure reducer unit test that imports the slice — a real cost, in
 * a suite that currently builds its own tiny store and touches none of that.
 *
 * Registration can arrive before or after the subscriber, and both orders occur:
 * the store subscribes at its own module scope, and the slice registers whenever
 * the canvas chunk loads. So the value is retained and replayed.
 */
let registration: WorkflowStoreRegistration | null = null
let subscriber: ((r: WorkflowStoreRegistration) => void) | null = null

export function registerWorkflowStore(next: WorkflowStoreRegistration): void {
  registration = next
  subscriber?.(next)
}

export function onWorkflowStoreRegistered(next: (r: WorkflowStoreRegistration) => void): void {
  subscriber = next
  if (registration) next(registration)
}
